/**
 * Every shut door in the product, as the **server** decided it.
 *
 * `GET /settings` carries a `gating` object beside the plan — see `Gating` in
 * `apps/api/src/plan.ts`, which is the contract this file reads and the document worth reading
 * first. Four maps, and the shape of each of them is the answer:
 *
 * - `features` — a `Gate` for each feature the plan does **not** include. A feature it does
 *   include is simply absent, so `gating.features.data_transfer` being there *is* the answer.
 * - `modules` — a `Gate` for each module the organisation does not hold.
 * - `limits` — the ceiling for each metered kind, each carrying its own `gate` once reached.
 * - `locks` — which existing records are open and which are behind a padlock.
 *
 * **A gate present means draw it, shut. A gate absent means the door is open.** Nothing in this
 * file — or in anything that reads it — compares a plan code, counts a limit, or works out for
 * itself which records are over an allowance. `packages/core/src/plans.ts` is the only file
 * allowed to define any of that, and a padlock decided in a component is a padlock that disagrees
 * with the API about which door is shut. That disagreement is not theoretical: the browser would
 * be deciding from a plan code cached in the session while the server decides from a count it has
 * just made.
 *
 * ## Permissions are not gates, and must never be turned into them
 *
 * The rule from ADR-0101, restated here because this is the file every screen calls:
 *
 * > **Filter by permission first and drop what fails; then look for a gate on what is left.**
 *
 * A junior without edit rights cannot fix a padlock with money — only their manager can — so a
 * padlock they will never be able to open is noise, and a screen full of that noise teaches people
 * to ignore every padlock on it, including the ones that would have earned a subscription. The
 * shorter form of the same rule, and the one worth checking a new padlock against:
 *
 * > **Draw a padlock only where paying would actually change what *this person* can do.**
 *
 * `useRecordLock` below is where that bites hardest, and it carries its own note.
 */

import { useQuery } from "@tanstack/react-query";
import type { Feature, ModuleCode } from "@rentorng/core";
import { api } from "./api";
import { useSession } from "./session";

/** Why something is shut. There are exactly three, and a permission is deliberately not one. */
export type GateReason = "plan" | "limit" | "module";

export interface Gate {
  reason: GateReason;
  /** What is shut, in the vocabulary of the reason — a feature, a limit key, or a module code. */
  subject: string;
  /**
   * One sentence, already user-facing.
   *
   * **Shown as it arrives.** Every padlock in the product says what the server said, so the
   * refusal a write gets and the padlock drawn before anybody presses anything cannot drift apart
   * into two different accounts of the same rule.
   */
  message: string;
  /** The plan the agency is on. Named by the server; never compared here. */
  current: { code: string; name: string };
  /** The cheapest plan that opens it — `null` when money is not what opens it at all. */
  needed: { code: string; name: string } | null;
}

/** A ceiling, and how close to it the agency is. `gate` is null until it is reached. */
export interface LimitCheck {
  allowed: boolean;
  used: number;
  limit: number;
  nearLimit: boolean;
  message?: string;
  gate: Gate | null;
}

/** The record kinds a plan ceiling can padlock. Listings deliberately are not one — ADR-0101. */
export type LockableLimit = "properties" | "units";

export interface OrgLock {
  limit: LockableLimit;
  allowance: number;
  total: number;
  /** True when there are more than the allowance, and therefore at least one padlock to draw. */
  over: boolean;
  /**
   * The ids that stay open, newest first.
   *
   * **`null` means every one of them is open**, which is the ordinary case — it is not a payload
   * listing six hundred ids to say "nothing is locked". Read it through `useRecordLock`, which
   * knows that.
   */
  unlocked: string[] | null;
  locked: string[];
  /** What the padlock says when it is pressed. Null while nothing on this kind is padlocked. */
  gate: Gate | null;
}

export interface Gating {
  features: Partial<Record<Feature, Gate>>;
  modules: Partial<Record<ModuleCode, Gate>>;
  limits: {
    properties: LimitCheck;
    units: LimitCheck;
    listings: LimitCheck;
    emailsPerMonth: LimitCheck;
  };
  locks: Record<LockableLimit, OrgLock>;
}

interface SettingsPayload {
  gating?: Gating;
}

/**
 * The whole gating object, or null while it is still on its way.
 *
 * Shares `["settings"]` with the settings screen rather than asking a padlock endpoint of its own.
 * Two reasons, and the second is the important one. A second request is a second round trip on
 * every page load for an answer that arrives with the plan anyway; and a screen that has to ask a
 * second question before it can draw a row **will draw the row unlocked for a moment first** — a
 * padlock that appears after the fact is worse than no padlock, because the reader has already
 * pressed the thing.
 *
 * `null` while it loads is deliberately the same answer as "nothing is gated". A padlock that
 * flickers into existence on every navigation would be the same fault said the other way round.
 */
export function useGating(): Gating | null {
  const { org } = useSession();
  const { data } = useQuery({
    queryKey: ["settings"],
    queryFn: () => api.get<SettingsPayload>("/settings"),
    enabled: Boolean(org),
    staleTime: 60_000,
  });
  return data?.gating ?? null;
}

/** A feature the plan does not include, or null when it does. */
export function useFeatureGate(feature: Feature): Gate | null {
  return useGating()?.features?.[feature] ?? null;
}

/** A module the organisation does not hold, or null when it does. */
export function useModuleGate(module: ModuleCode): Gate | null {
  return useGating()?.modules?.[module] ?? null;
}

/** A ceiling and how close it is. Null while the answer is still on its way. */
export function useLimit(limit: keyof Gating["limits"]): LimitCheck | null {
  return useGating()?.limits?.[limit] ?? null;
}

export interface RecordLock {
  /** True when there is at least one padlock to draw on this kind of record. */
  over: boolean;
  /** Whether this particular record's screen is shut — for **this** person. */
  isLocked: (id: string) => boolean;
  /** What the padlock says when it is pressed. Null when there is nothing to draw. */
  gate: Gate | null;
  /** How many stay open. Useful for the sentence beside a list, never for deciding a row. */
  allowance: number;
  total: number;
}

/**
 * Which of an agency's records are padlocked on screen, for the person looking at the screen.
 *
 * ## The padlock is drawn only for somebody who could otherwise act on the row
 *
 * This is the permission rule from ADR-0101 applied to the one place it is easiest to get wrong,
 * and the reasoning is worth the paragraph.
 *
 * **What the padlock actually refuses is a write.** Every `assertUnlocked` in the API sits on a
 * write route — editing, deleting, publishing, letting, adding a unit to a building — and every
 * one of those routes already requires `property.edit`. Nothing about a padlock changes what a
 * viewer, an accountant or a portfolio agent without edit rights can see or reach: they could not
 * edit that unit yesterday, they cannot edit it today, and the agency paying us would not change
 * that. Drawing them a padlock would be drawing a lock on a door that was never theirs to open —
 * and one that no amount of money opens for them.
 *
 * So the test a padlock has to pass, everywhere and not only here: **would paying change what
 * this person can do?** If not, it is a permission wearing a padlock's clothes, and it stays off
 * the screen. Fill a junior's screen with those and people learn to ignore every padlock on it,
 * including the ones that would have earned a subscription.
 *
 * **What this is not.** It is not a filter and it is not a hidden row. The record is drawn either
 * way, in full, in the same list, in the same order — the padlock is a mark on it and a different
 * destination when it is pressed. Nothing is deleted, hidden, archived or unpublished, and the
 * money on a padlocked record goes on moving exactly as before. See `planLocks` in
 * `packages/core/src/plans.ts` for the warning this whole design exists to honour.
 */
export function useRecordLock(kind: LockableLimit): RecordLock {
  const gating = useGating();
  const { can } = useSession();
  const view = gating?.locks?.[kind] ?? null;

  /* Permission first, gate second — and here that means the whole answer collapses to "nothing is
     locked" for anybody who could not have written to these records in the first place. */
  const mayAct = can("property.edit");
  const over = Boolean(view?.over) && mayAct;

  return {
    over,
    /* `unlocked === null` is the server saying every record is open, which is the ordinary case
       and the reason the payload does not list them. Asking `locked.includes` instead would be a
       second reading of the same answer, and it goes wrong on exactly the page nobody tests: a
       list showing a record the server did not have when it computed the lock. */
    isLocked: (id: string) => (over && view?.unlocked ? !view.unlocked.includes(id) : false),
    gate: over ? (view?.gate ?? null) : null,
    allowance: view?.allowance ?? Number.POSITIVE_INFINITY,
    total: view?.total ?? 0,
  };
}
